import dayjs from 'dayjs';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { EventTicket, EventTicketStatus } from './event-ticket.js';
import { TicketOrderStatus } from './ticket-order-status.js';
import { OnlineEventTicketOrder } from './online-event-ticket-order.js';

import {
  cloneData,
  cloneDataOrNull,
  cloneListOfCloneable,
  DataModel,
  DataModelBuilder,
  fromData,
  fromDataOrNull,
  isSameData,
  isSameList,
  SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { PaymentType } from './payment-type.js';
import { VersionRange } from '../core/version-range.js';
import { ComponentList } from '../../components/core/component-list.js';
import { PaymentStatus } from './payment-status.js';
import { Discount } from './discounts/discount.js';
import { LegacyCustomerOrderBase } from '../../contracts/customer-order.js';
import { Language } from './language.js';
import { getDefaultPaymentMethodForPaymentType, PaymentMethod } from './payment-method.js';
import { OrderSource } from './order-source.js';
import { CustomerProfile } from './customer-profile.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { Refund } from './discounts/refund.js';
import { SearchUtils } from '../../utility/search-utils.js';
import { EntityMixin } from '../../components/core/entity-mixin.js';
import { OfflinePaymentDetails } from './offline-payment-details.js';
import { Price } from './price.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { MigrationSafe } from '../core/migration-safe.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { ContactDetails } from './contact-details.js';
import { ProductOrderItem } from './product-order-item.js';
import { EventBookingStrategy } from './event-booking-protocol/event-booking-strategy.js';
import { Country } from './country.js';
import { SerializationException } from '../../exceptions.js';
import { OrderDomain } from './order-domain.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { OrderSideEffectType } from './orders/order-side-effect-type.js';
import { OfflineReceiptType } from './invoicing/offline-receipt-type.js';

export class EventTicketOrder extends EntityMixin(DataModel) implements LegacyCustomerOrderBase {
  static readonly TYPE_ID: string = '4640f59adb5d4d6c9d62d3aba47c7115';
  static readonly SCHEMA_VERSION: number = 23;

  public readonly isLegacyOrder = true;

  public readonly customerId: string | null;
  public readonly userId: string | null;
  public readonly language: Language | null;
  public readonly country: Country;
  public readonly timezone: TimeZoneRegion;
  public readonly eventId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly promoterId: string | null,
    public readonly tickets: EventTicket[],
    public readonly products: ProductOrderItem[],
    public readonly status: TicketOrderStatus,
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly offlinePaymentDetails: OfflinePaymentDetails | null,
    public readonly discount: Discount | null,
    public readonly source: OrderSource,
    public readonly domain: OrderDomain,
    public readonly components: ComponentList,
    public readonly isGuestOrder: boolean,
    public readonly refunds: Refund[],
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly standardApplicationFee: MigrationSafe<Price>,
    public readonly standardApplicationFeeVat: MigrationSafe<InvoiceVat>,
    public readonly productApplicationFee: Price | null,
    public readonly productApplicationFeeVat: InvoiceVat | null,
    public readonly donation: Price | null,
    public readonly bookingStrategy: EventBookingStrategy,
    public readonly customer: CustomerProfile | null,
    public readonly contactDetails: ContactDetails,
    public readonly sideEffects: OrderSideEffect[],
    public readonly allowOfflineOrderReceipts: boolean | null,
    public readonly receiptType: OfflineReceiptType | null,
  ) {
    super();
    this.eventId = tickets[0].eventId;
    this.userId = tickets[0].userId;
    this.customerId = tickets[0].customerId;
    this.language = tickets[0].language;
    this.country = tickets[0].country;
    this.timezone = tickets[0].timezone;
  }

  get $typeId(): string {
    return EventTicketOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(18, EventTicketOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === TicketOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === TicketOrderStatus.Canceled;
  }

  static create(opts: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    businessRef: BusinessModuleRef,
    promoterId: string | null,
    tickets: EventTicket[],
    products: ProductOrderItem[],
    status: TicketOrderStatus,
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    onlinePaymentDetails: OnlinePaymentDetails | null,
    offlinePaymentDetails: OfflinePaymentDetails | null,
    discount: Discount | null,
    source: OrderSource,
    domain: OrderDomain,
    components: ComponentList,
    isGuestOrder: boolean,
    refunds: Refund[],
    donation: Price | null,
    standardApplicationFee: Price | null,
    standardApplicationFeeVat: InvoiceVat | null,
    productApplicationFee: Price | null,
    productApplicationFeeVat: InvoiceVat | null,
    bookingStrategy: EventBookingStrategy,
    customer: CustomerProfile | null,
    contactDetails: ContactDetails,
    allowOfflineOrderReceipts: boolean | null,
    receiptType: OfflineReceiptType | null,
    sideEffects?: OrderSideEffect[],
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
  }): EventTicketOrder {
    return new EventTicketOrder(
        opts.uniqueId,
        opts.creatorId,
        opts.creationDate,
        opts.businessRef,
        opts.promoterId,
        opts.tickets,
        opts.products,
        opts.status,
        opts.paymentType,
        opts.paymentMethod,
        opts.onlinePaymentDetails,
        opts.offlinePaymentDetails,
        opts.discount,
        opts.source,
        opts.domain,
        opts.components,
        opts.isGuestOrder,
        opts.refunds,
        opts.cancelledDate ?? null,
        opts.cancelledBy ?? null,
        MigrationSafe.defined(opts.standardApplicationFee),
        MigrationSafe.defined(opts.standardApplicationFeeVat),
        opts.productApplicationFee,
        opts.productApplicationFeeVat,
        opts.donation,
        opts.bookingStrategy,
        opts.customer,
        opts.contactDetails,
        opts.sideEffects ?? [],
        opts.allowOfflineOrderReceipts,
        opts.receiptType,
    );
  }

  static fromOnlineOrder(order: OnlineEventTicketOrder): EventTicketOrder {
    return EventTicketOrder.create({
      uniqueId: order.uniqueId,
      creatorId: order.creatorId,
      creationDate: order.creationDate,
      businessRef: order.businessRef.clone(),
      promoterId: order.tickets[0].promoterId,
      tickets: cloneListOfCloneable(order.tickets),
      products: cloneListOfCloneable(order.products),
      status: order.status,
      paymentType: order.paymentType,
      paymentMethod: order.paymentMethod,
      onlinePaymentDetails: order.onlinePaymentDetails?.clone() ?? null,
      offlinePaymentDetails: null,
      discount: order.discount?.clone() ?? null,
      source: OrderSource.Client,
      domain: order.domain,
      components: order.components.clone(),
      isGuestOrder: order.isGuestOrder,
      refunds: cloneListOfCloneable(order.refunds),
      cancelledDate: TimeZone.cloneDateOrNull(order.cancelledDate),
      cancelledBy: order.cancelledBy,
      standardApplicationFee: order.standardApplicationFee?.clone() ?? null,
      standardApplicationFeeVat: order.standardApplicationFeeVat,
      productApplicationFee: order.productApplicationFee?.clone() ?? null,
      productApplicationFeeVat: order.productApplicationFeeVat,
      donation: order.donation?.clone() ?? null,
      bookingStrategy: order.bookingStrategy,
      customer: order.customer,
      contactDetails: order.contactDetails,
      allowOfflineOrderReceipts: null,
      receiptType: null,
      sideEffects: [], // do not copy over side effects from online order! the functionalities are independent
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventTicketOrder {
    return new EventTicketOrder(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'promoterId'),
        MapUtils.getList<EventTicket>(map, 'tickets', (value) => {
          return context.fromData<EventTicket>(value as SerializedData);
        }),
        MapUtils.getList<ProductOrderItem>(map, 'products', (value) => {
          return context.fromData<ProductOrderItem>(value as SerializedData);
        }),
        toEnumFromString(MapUtils.get<string>(map, '$status'), TicketOrderStatus),
        toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType),
        toEnumFromString(MapUtils.get<string>(map, 'paymentMethod'), PaymentMethod),
        context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'onlinePaymentDetails')),
        context.fromDataOrNull<OfflinePaymentDetails>(MapUtils.getOrNull<SerializedData>(map, 'offlinePaymentDetails')),
        context.fromDataOrNull<Discount>(MapUtils.getOrNull<SerializedData>(map, 'discount')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), OrderSource),
        toEnumFromString(MapUtils.get<string>(map, 'domain'), OrderDomain),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
        MapUtils.get<boolean>(map, 'isGuestOrder'),
        MapUtils.getList<Refund>(map, 'refunds', (value) => {
          return context.fromData<Refund>(value as SerializedData);
        }),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
        MigrationSafe.decode<Price, string>(MapUtils.getRaw(map, 'standardApplicationFee'), (data) => Price.decode(data)),
        MigrationSafe.decode<InvoiceVat, string>(MapUtils.getRaw(map, 'standardApplicationFeeVat'), (data) => toEnumFromString(data, InvoiceVat)),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'productApplicationFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'productApplicationFeeVat'), InvoiceVat),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'donation')),
        toEnumFromString(MapUtils.getString(map, 'bookingStrategy'), EventBookingStrategy),
        context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer')),
        context.fromData<ContactDetails>(MapUtils.getRecord(map, 'contactDetails')),
        MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(value as SerializedData)),
        MapUtils.getBooleanOrNull(map, 'allowOfflineOrderReceipts'),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'receiptType'), OfflineReceiptType),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    context.storage.set('avoidOptimizeForQuery', true);

    const map = {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'customerId': this.customerId,
      'userId': this.userId,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'promoterId': this.promoterId,
      'tickets': this.tickets.map((e) => context.toData(e)),
      'products': this.products.map((e) => context.toData(e)),
      '$status': fromEnumAsString(this.status),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'offlinePaymentDetails': context.toDataOrNull(this.offlinePaymentDetails),
      'discount': context.toDataOrNull(this.discount),
      'source': fromEnumAsString(this.source),
      'domain': fromEnumAsString(this.domain),
      'components': this.components.serialize(context),
      'isGuestOrder': this.isGuestOrder,
      'refunds': this.refunds.map((e) => context.toData(e)),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'eventId': this.eventId,
      'standardApplicationFee': this.standardApplicationFee.encode((v) => v.encode()),
      'standardApplicationFeeVat': this.standardApplicationFeeVat.encode((v) => fromEnumAsString(v)),
      'productApplicationFee': this.productApplicationFee?.encode() ?? null,
      'productApplicationFeeVat': fromEnumAsStringOrNull(this.productApplicationFeeVat),
      'donation': this.donation?.encode() ?? null,
      'bookingStrategy': fromEnumAsString(this.bookingStrategy),
      'customer': context.toDataOrNull(this.customer),
      'contactDetails': context.toData(this.contactDetails),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
      'receiptType': fromEnumAsStringOrNull(this.receiptType),
    };

    context.storage.remove('avoidOptimizeForQuery');
    return map;
  }

  clone(): this {
    return this.copyWith({});
  }

  isSame(other: EventTicketOrder): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.promoterId === this.promoterId &&
      other.creationDate === this.creationDate &&
      other.businessRef === this.businessRef &&
      other.paymentType === this.paymentType &&
      other.paymentMethod === this.paymentMethod &&
      other.cancelledDate === this.cancelledDate &&
      other.cancelledBy === this.cancelledBy &&
      isSameData(other.donation, this.donation) &&
      other.standardApplicationFee.isSame(this.standardApplicationFee, isSameData) &&
      other.standardApplicationFeeVat.isSame(this.standardApplicationFeeVat) &&
      isSameData(other.productApplicationFee, this.productApplicationFee) &&
      other.productApplicationFeeVat == this.productApplicationFeeVat &&
      isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
      isSameData(other.offlinePaymentDetails, this.offlinePaymentDetails) &&
      isSameList(other.tickets, this.tickets, isSameData) &&
      isSameList(other.products, this.products, isSameData) &&
      other.status === this.status &&
      other.bookingStrategy === this.bookingStrategy &&
      isSameData(other.discount, this.discount) &&
      isSameData(other.customer, this.customer) &&
      isSameData(other.contactDetails, this.contactDetails) &&
      other.source === this.source &&
      other.domain === this.domain &&
      other.isGuestOrder === this.isGuestOrder &&
      other.components.isSame(this.components) &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts &&
      other.receiptType === this.receiptType &&
      isSameList(other.refunds, this.refunds, isSameData);
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    businessRef = cloneData(this.businessRef),
    promoterId = this.promoterId,
    tickets = cloneListOfCloneable(this.tickets),
    products = cloneListOfCloneable(this.products),
    status = this.status,
    paymentType = this.paymentType,
    paymentMethod = this.paymentMethod,
    onlinePaymentDetails = cloneDataOrNull(this.onlinePaymentDetails),
    offlinePaymentDetails = cloneDataOrNull(this.offlinePaymentDetails),
    discount = cloneDataOrNull(this.discount),
    source = this.source,
    domain = this.domain,
    components = cloneData(this.components),
    isGuestOrder = this.isGuestOrder,
    refunds = cloneListOfCloneable(this.refunds),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    standardApplicationFee = this.standardApplicationFee.clone((v) => v.clone()),
    standardApplicationFeeVat = this.standardApplicationFeeVat.clone((v) => v),
    productApplicationFee = cloneDataOrNull(this.productApplicationFee),
    productApplicationFeeVat = this.productApplicationFeeVat,
    donation = cloneDataOrNull(this.donation),
    bookingStrategy = this.bookingStrategy,
    customer = cloneDataOrNull(this.customer),
    contactDetails = cloneData(this.contactDetails),
    sideEffects = this.sideEffects.map((e) => e.clone()),
    allowOfflineOrderReceipts = this.allowOfflineOrderReceipts,
    receiptType = this.receiptType,
  }: {
    uniqueId?: string,
    creatorId?: string,
    creationDate?: dayjs.Dayjs,
    businessRef?: BusinessModuleRef,
    promoterId?: string | null,
    tickets?: EventTicket[],
    products?: ProductOrderItem[],
    status?: TicketOrderStatus,
    paymentType?: PaymentType,
    paymentMethod?: PaymentMethod,
    onlinePaymentDetails?: OnlinePaymentDetails | null,
    offlinePaymentDetails?: OfflinePaymentDetails | null,
    discount?: Discount | null,
    source?: OrderSource,
    domain?: OrderDomain,
    components?: ComponentList,
    isGuestOrder?: boolean,
    refunds?: Refund[],
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
    standardApplicationFee?: MigrationSafe<Price>,
    standardApplicationFeeVat?: MigrationSafe<InvoiceVat>,
    productApplicationFee?: Price | null,
    productApplicationFeeVat?: InvoiceVat | null,
    donation?: Price | null,
    bookingStrategy?: EventBookingStrategy,
    customer?: CustomerProfile | null,
    contactDetails?: ContactDetails,
    sideEffects?: OrderSideEffect[],
    allowOfflineOrderReceipts?: boolean | null,
    receiptType?: OfflineReceiptType | null,
  } = {}): this {
    return new EventTicketOrder(
        uniqueId,
        creatorId,
        creationDate,
        businessRef,
        promoterId,
        tickets,
        products,
        status,
        paymentType,
        paymentMethod,
        onlinePaymentDetails,
        offlinePaymentDetails,
        discount,
        source,
        domain,
        components,
        isGuestOrder,
        refunds,
        cancelledDate,
        cancelledBy,
        standardApplicationFee,
        standardApplicationFeeVat,
        productApplicationFee,
        productApplicationFeeVat,
        donation,
        bookingStrategy,
        customer,
        contactDetails,
        sideEffects,
        allowOfflineOrderReceipts,
        receiptType,
    ) as this;
  }

  copyAsOpen({ shouldClearRefunds = false }: {
    shouldClearRefunds?: boolean,
  } = {}): this {
    return this.copyWith({
      status: TicketOrderStatus.Open,
      tickets: this.tickets.map((ticket) => ticket.markAsRestored()),
      cancelledBy: null,
      cancelledDate: null,
      refunds: shouldClearRefunds ? [] : undefined,
    });
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: TicketOrderStatus.Aborted,
      tickets: this.tickets.map((ticket) => ticket.copyAsAborted()),
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: dayjs.Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? TicketOrderStatus.Refunded : TicketOrderStatus.Canceled,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      cancelledDate: this.cancelledDate ?? cancelledDate,
      tickets: this.tickets.map((ticket) => {
        const shouldRefund = isRefund || ticket.status === EventTicketStatus.Refunded;

        return ticket.copyAsCanceled(cancelledBy, cancelledDate, shouldRefund, { shouldCancelSupplement: true });
      }),
    });
  }

  copyWithDiscount(discount: Discount): this {
    return this.copyWith({
      discount: discount,
    });
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return this.copyWith({
      onlinePaymentDetails: paymentDetails,
    });
  }

  copyWithOfflinePaymentDetails(offlinePaymentDetails: OfflinePaymentDetails): this {
    return this.copyWith({
      offlinePaymentDetails: offlinePaymentDetails,
    });
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return this.copyWith({
      onlinePaymentDetails: this.onlinePaymentDetails?.copyWith({ status }),
    });
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return this.copyWith({
      paymentMethod: paymentMethod,
    });
  }

  copyWithContactDetails(contactDetails: ContactDetails): this {
    return this.copyWith({
      contactDetails: contactDetails,
    });
  }

  copyWithTicketCloner(cloner: (t: EventTicket) => EventTicket): this {
    return this.copyWith({ tickets: this.tickets.map(cloner) });
  }

  copyWithCreationDate(creationDate: dayjs.Dayjs): this {
    return this.copyWith({
      creationDate: creationDate,
      tickets: this.tickets.map((e) => e.copyWith({
        creationDate: creationDate,
      })),
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      creatorId: account.uniqueId,
      customer: this.customer?.assignToCustomerAccount(account),
      tickets: this.tickets.map((e) => e.assignToCustomerAccount(account)),
    });
  }

  getTotalFee(): Price | null {
    // This is an order with no fee information. We will return [null].
    if(!this.standardApplicationFee.isDefined) {
      return null;
    }

    if(this.standardApplicationFee.isSome && this.productApplicationFee != null) {
      return this.standardApplicationFee.value.add(this.productApplicationFee);
    }
    else if(this.standardApplicationFee.isSome) {
      return this.standardApplicationFee.value.clone();
    }
    else if(this.productApplicationFee != null) {
      return this.productApplicationFee.clone();
    }

    return null;
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.customer;
  }

  generateSearchQueryArray() : string[] {
    return SearchUtils.generateSearchQueryArray([
      this.uniqueId,
      this.onlinePaymentDetails?.paymentRequestId,
      this.onlinePaymentDetails?.paymentId,
      this.customer?.uniqueId ?? this.customerId,
      this.customer?.firstName,
      this.customer?.lastName,
      this.customer?.email,
    ], this.tickets.map((e) => {return e.templateId;}));
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }

  static encodedStatusSafe(status: TicketOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof EventTicketOrder;
  }

  static assertType(order: DataModel): order is EventTicketOrder {
    return order instanceof EventTicketOrder;
  }
}

export class EventTicketOrderBuilder extends DataModelBuilder<EventTicketOrder> {
  build(context: SerializationContext, map: SerializedData): EventTicketOrder {
    return EventTicketOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: EventTicketOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'search': model.generateSearchQueryArray(),
      'ticketType': fromEnumAsString(model.tickets[0].ticketType),
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'containsEventProducts': model.bookingStrategy === EventBookingStrategy.TicketsAndProducts && model.products.length > 0,
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    MapUtils.replace(map, '$status', 'DELETED', fromEnumAsString(TicketOrderStatus.Canceled));
    this.migrateEnum(map, '$status', TicketOrderStatus, TicketOrderStatus.Open);
    this.migrateEnum(map, 'paymentType', PaymentType, PaymentType.OnDelivery);
    this.migrateEnum(map, 'paymentMethod', PaymentMethod, PaymentMethod.Cash);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(EventTicketOrder.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(EventTicketOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    const legacyTicketData = version < 18 ?
      this.extractCustomerAndContactDetailsFromLegacyTicketData(version, map) : null;

    // This version was introduced to sync broken schema versions between typescript and dart.
    // Also in this version `ticketType` and `customerEmail` was introduced.
    if(version < 2) version = 2;

    // In this version components were introduced.
    if(version < 3) version = 3;

    // In this version `promoterId` of type `string | null` was introduced.
    if(version < 4) version = 4;

    // In version 5 we added `discount` of type `Discount | null`.
    if(version < 5) version = 5;

    // In version 6 `userId` is auto-detected from tickets.
    if(version < 6) version = 6;

    // In this version parameter `paymentMethod` of type `PaymentMethod` was
    // introduced.
    if(version < 7) {
      version = 7;

      const paymentType: PaymentType = toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType);
      map['paymentMethod'] = fromEnumAsString(getDefaultPaymentMethodForPaymentType(paymentType));
    }

    // In this version parameter `source` of type `OrderSource` was
    // introduced.
    if(version < 8) {
      if(MapUtils.get<string>(map, 'paymentMethod') === fromEnumAsString(PaymentMethod.OnlineBankCard)) {
        map['source'] = fromEnumAsString(OrderSource.Client);
      }
      else if(MapUtils.get<string>(map, 'paymentType') === fromEnumAsString(PaymentType.Online)) {
        map['source'] = fromEnumAsString(OrderSource.Client);
      }
      else {
        const customerId = legacyTicketData?.customer?.uniqueId ?? null;
        const userId = legacyTicketData?.customer?.userId ?? null;
        const creatorId = MapUtils.getString(map, 'creatorId');

        if(userId === creatorId) {
          map['source'] = fromEnumAsString(OrderSource.Client);
        }
        else if(customerId === null) {
          map['source'] = fromEnumAsString(OrderSource.Admin);
        }
        else if(userId === null) {
          map['source'] = fromEnumAsString(OrderSource.Admin);
        }
        else {
          map['source'] = fromEnumAsString(OrderSource.Admin);
        }
      }

      version = 8;
    }

    // In version 9 `isGuestOrder` of type `boolean` was added.
    if(version < 9) {
      map['isGuestOrder'] = false;
      version = 9;
    }

    // In version 10 we added `refunds` of type `Refund[]`.
    if(version < 10) {
      version = 10;
      map['refunds'] = [];
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 11) {
      version = 11;
    }

    /**
     * Version 12:
     * - add `offlinePaymentDetails` of type: {@link OfflinePaymentDetails} | null.
     */
    if(version < 12) {
      version = 12;
    }

    /**
     * Version 13:
     * - add `applicationFee` of type: {@link Price} | null.
     * - add `applicationFeeVat` of type: {@link InvoiceVat} | null.
     */
    if(version < 13) {
      map['applicationFee'] = MigrationSafe.UNDEFINED_STATUS;
      map['applicationFeeVat'] = MigrationSafe.UNDEFINED_STATUS;
      version = 13;
    }

    // In version 14 we added `donation` of type `Price | null`.
    if(version < 14) version = 14;

    // In version 15
    // - we added `products` of type `ProductOrderItem[]`.
    // - we added `bookingStrategy` of type `EventBookingStrategy`.
    if(version < 15) {
      map['products'] = [];
      map['bookingStrategy'] = fromEnumAsString(EventBookingStrategy.Tickets);
      version = 15;
    }

    // In version 16
    // - we added `productApplicationFee` of type `Price | null`.
    // - we added `productApplicationFeeVat` of type `InvoiceVat | null`.
    if(version < 16) version = 16;

    // In version 17
    // - we renamed `applicationFee` to `standardApplicationFee`.
    // - we renamed `applicationFeeVat` to `standardApplicationFeeVat`.
    if(version < 17) {
      map['standardApplicationFee'] = map['applicationFee'];
      map['standardApplicationFeeVat'] = map['applicationFeeVat'];
      version = 17;
    }

    // In version 18
    // - we deprecated customer and contact details data from event ticket.
    // - we added standalone customer and contact details fields.
    if(version < 18) {
      map['customer'] = context.toDataOrNull(legacyTicketData!.customer);
      map['contactDetails'] = context.toData(legacyTicketData!.contactDetails);

      version = 18;
    }

    // In version 19
    // - we removed the `customer` and `contactDetails` fields from the ticket model
    if(version < 19) version = 19;

    // In version 20
    // - we added the `domain` field of type `OrderDomain`
    if(version < 20) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 20;
    }

    // In version 21
    // - we added `sideEffects` of type `OrderSideEffect[]`.
    if(version < 21) {
      map['sideEffects'] = [];
      version = 21;
    }

    // In version 22 we added allowOfflineOrderReceipts of type boolean | null.
    if(version < 22) {
      map['allowOfflineOrderReceipts'] = null;
      version = 22;
    }

    // In version 23 we added `receiptType` of type `OfflineReceiptType | null`.
    if(version < 23) {
      map['receiptType'] = null;
      version = 23;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  private extractCustomerAndContactDetailsFromLegacyTicketData(schemaVersion: number, map: SerializedData): {
    customer: CustomerProfile | null,
    contactDetails: ContactDetails,
  } {
    // Customer is stored in the order starting with schema version {18}.
    if(schemaVersion >= 18) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [EventTicketOrder]. Current schema version no longer stores the data in tickets.',
          { schemaVersion }
      );
    }

    // It's important that you do not do any serialization/deserialization here so as to not modify the ticket maps.
    // Modifying the maps will lead to migration corruption.
    const tickets = MapUtils.getList<SerializedData>(map, 'tickets', (value) => value as SerializedData);
    if(tickets.length <= 0) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [EventTicketOrder]. Order has no tickets.',
          { schemaVersion }
      );
    }

    const ticketData = tickets[0];
    const customerId = MapUtils.getStringOrNull(ticketData, 'customerId');
    const customerData = MapUtils.getRecordOrNull(ticketData, 'customer');
    const contactDetailsData = MapUtils.getRecordOrNull(ticketData, 'contactDetails');

    if(customerId != null && customerData == null) {
      throw new SerializationException(
          'Failed to extract customer from tickets of [EventTicketOrder]. Ticket and order schema versions are out of sync.',
          { orderSchema: schemaVersion, ticketSchema: ticketData['$schema_version'] ?? null }
      );
    }

    if(contactDetailsData == null) {
      throw new SerializationException(
          'Failed to extract contact details from tickets of [EventTicketOrder]. Ticket and order schema versions are out of sync.',
          { orderSchema: schemaVersion, ticketSchema: ticketData['$schema_version'] ?? null }
      );
    }

    return {
      customer: fromDataOrNull<CustomerProfile>(customerData),
      contactDetails: fromData<ContactDetails>(contactDetailsData),
    };
  }
}
